import { type JSX, useEffect, useRef } from "react";
import type { Html5Qrcode } from "html5-qrcode";

export const CAMERA_READER_ELEMENT_ID = "scan-camera-reader";
export const CAMERA_UNAVAILABLE_MESSAGE =
  "The camera is not available. Allow camera access in the browser, or type or scan the code into the box.";

interface CameraReaderProps {
  onDecode: (text: string) => void;
  onError: (message: string) => void;
}

/**
 * An html5-qrcode reader (QR and the common 1D codes) on the rear camera. Loaded on demand, and
 * always stopped and cleared on unmount, including when unmount comes while start is pending, so
 * leaving the page never leaves the camera running.
 */
export default function CameraReader({
  onDecode,
  onError,
}: CameraReaderProps): JSX.Element {
  const onDecodeRef = useRef(onDecode);
  const onErrorRef = useRef(onError);
  onDecodeRef.current = onDecode;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    let reader: Html5Qrcode | null = null;
    let running = false;

    const release = (instance: Html5Qrcode): void => {
      Promise.resolve()
        .then(() => instance.stop())
        .catch(() => undefined)
        .finally(() => {
          try {
            instance.clear();
          } catch {
            // Already cleared: nothing left to release.
          }
        });
    };

    (async () => {
      try {
        const { Html5Qrcode: Reader } = await import("html5-qrcode");
        if (cancelled) return;
        reader = new Reader(CAMERA_READER_ELEMENT_ID);
        await reader.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          // html5-qrcode still reports a frame it was decoding when stop() ran: drop it.
          text => {
            if (!cancelled) onDecodeRef.current(text);
          },
          () => undefined
        );
        running = true;
        if (cancelled) release(reader);
      } catch {
        if (!cancelled) onErrorRef.current(CAMERA_UNAVAILABLE_MESSAGE);
      }
    })();

    return () => {
      cancelled = true;
      if (reader && running) release(reader);
    };
  }, []);

  return (
    <div
      id={CAMERA_READER_ELEMENT_ID}
      className="mt-4 w-full max-w-sm overflow-hidden rounded-md"
    />
  );
}
